// frontend/src/App.tsx
import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { Toaster } from 'sonner';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import { useSiteSettingsStore } from './store/settingsStore'; 

// --- Page Imports ---
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ArticlesPage from './pages/ArticlesPage';
import ArticleDetailPage from './pages/ArticleDetailPage';
import ResearchPage from './pages/ResearchPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import ManageArticlesPage from './pages/admin/ManageArticlesPage';
import CreateArticlePage from './pages/admin/CreateArticlePage';
import EditArticlePage from './pages/admin/EditArticlePage';
import ManageResearchPage from './pages/admin/ManageResearchPage';
import CreateResearchPage from './pages/admin/CreateResearchPage';
import EditResearchPage from './pages/admin/EditResearchPage';
import ManageProductsPage from './pages/admin/ManageProductsPage';
import CreateProductPage from './pages/admin/CreateProductPage';
import EditProductPage from './pages/admin/EditProductPage';
import ManageUsersPage from './pages/admin/ManageUsersPage';
import CreateUserPage from './pages/admin/CreateUserPage';
import EditUserPage from './pages/admin/EditUserPage';
import ManageTagsPage from './pages/admin/ManageTagsPage'; 
import ViewAnalyticsPage from './pages/admin/ViewAnalyticsPage';
import SiteSettingsPage from './pages/admin/SiteSettingsPage';
import ManageContactSubmissionsPage from "@/pages/admin/ManageContactSubmissionsPage";
import EditAboutUsPage from './pages/admin/EditAboutUsPage';
import EditHomepageContentPage from './pages/admin/EditHomepageContentPage'; // <-- NEW IMPORT

import NotFoundPage from './pages/NotFoundPage';
import ProtectedRoute from './components/ProtectedRouter';
import MaintenancePage from './pages/MaintenancePage';


const AdminLayout = () => (
  <div className="admin-area">
    <Outlet />
  </div>
);

const GaPageTracker = () => {
  const location = useLocation();
  const publicSettings = useSiteSettingsStore(state => state.publicSettings);
  const isLoadingSettings = useSiteSettingsStore(state => state.isLoading);
  const [isGaInitialized, setIsGaInitialized] = useState(false);

  useEffect(() => {
    if (!isLoadingSettings && publicSettings?.ga_tracking_id) {
      const gaId = publicSettings.ga_tracking_id;
      if (gaId && gaId.startsWith('G-') && gaId.length > 5 && !isGaInitialized) {
        try {
          ReactGA.initialize(gaId);
          setIsGaInitialized(true);
          ReactGA.send({ hitType: "pageview", page: location.pathname + location.search, title: document.title });
        } catch (error) {
          console.error("Error initializing react-ga4:", error);
        }
      } else if (gaId && (gaId === "UA-XXXXX-Y" || gaId === "G-XXXXXXX" || !gaId.startsWith('G-'))) {
        setIsGaInitialized(false);
        console.warn("GA ID is a placeholder or invalid, GA not initialized:", gaId);
      }
    }
  }, [isLoadingSettings, publicSettings, isGaInitialized, location.pathname, location.search]);

  useEffect(() => {
    if (isGaInitialized) {
      ReactGA.send({ hitType: "pageview", page: location.pathname + location.search, title: document.title });
    }
  }, [location, isGaInitialized]);

  return null;
};

const AppContentController = () => {
  const location = useLocation();
  const publicSettings = useSiteSettingsStore(state => state.publicSettings);
  const isLoadingSettings = useSiteSettingsStore(state => state.isLoading);
  
  const isUserAdmin = () => localStorage.getItem('userRole') === 'admin';

  if (isLoadingSettings && publicSettings === undefined) {
    return <div className="flex items-center justify-center min-h-screen">Loading Site...</div>;
  }

  const maintenanceModeEnabled = publicSettings?.maintenance_mode ?? false;
  const maintenanceMessage = publicSettings?.maintenance_message || 'Our site is currently down for maintenance. Please check back soon!';

  const productsPageEnabled = publicSettings?.page_products_enabled ?? true;
  const researchPageEnabled = publicSettings?.page_research_enabled ?? true;
  const articlesPageEnabled = publicSettings?.page_articles_enabled ?? true;
  const aboutPageEnabled = publicSettings?.page_about_enabled ?? true;
  const contactPageEnabled = publicSettings?.page_contact_enabled ?? true;
  const registrationPageEnabled = publicSettings?.page_registration_enabled ?? true;

  const authPaths = ['/login', '/register'];

  if (maintenanceModeEnabled && !isUserAdmin() && !authPaths.includes(location.pathname)) {
    return <MaintenancePage message={maintenanceMessage} />;
  }

  return (
    <div className="flex flex-col min-h-screen text-foreground bg-background">
      <Header />
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<HomePage />} />
          
          {productsPageEnabled ? <Route path="/products" element={<ProductsPage />} /> : <Route path="/products" element={<NotFoundPage />} />}
          {productsPageEnabled ? <Route path="products/:productIdOrSlug" element={<ProductDetailPage />} /> : <Route path="products/:productIdOrSlug" element={<NotFoundPage />} />}
          
          {articlesPageEnabled ? <Route path="/articles" element={<ArticlesPage />} /> : <Route path="/articles" element={<NotFoundPage />} />}
          {articlesPageEnabled ? <Route path="/articles/:articleSlug" element={<ArticleDetailPage />} /> : <Route path="/articles/:articleSlug" element={<NotFoundPage />} />}

          {researchPageEnabled ? <Route path="/research" element={<ResearchPage />} /> : <Route path="/research" element={<NotFoundPage />} />}
          
          {aboutPageEnabled ? <Route path="/about" element={<AboutPage />} /> : <Route path="/about" element={<NotFoundPage />} />}
          
          {contactPageEnabled ? <Route path="/contact" element={<ContactPage />} /> : <Route path="/contact" element={<NotFoundPage />} />}
          
          <Route path="/login" element={<LoginPage />} />
          {registrationPageEnabled ? <Route path="/register" element={<RegisterPage />} /> : <Route path="/register" element={<NotFoundPage />} />}

          <Route element={<ProtectedRoute adminOnly />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboardPage />} />
              <Route path="dashboard" element={<AdminDashboardPage />} />
              <Route path="articles" element={<ManageArticlesPage />} />
              <Route path="articles/new" element={<CreateArticlePage />} />
              <Route path="articles/edit/:articleId" element={<EditArticlePage />} />
              <Route path="research" element={<ManageResearchPage />} />
              <Route path="research/new" element={<CreateResearchPage />} />
              <Route path="research/edit/:researchId" element={<EditResearchPage />} />
              <Route path="products" element={<ManageProductsPage />} />
              <Route path="products/new" element={<CreateProductPage />} />
              <Route path="products/edit/:productIdOrSlug" element={<EditProductPage />} />
              <Route path="users" element={<ManageUsersPage />} />
              <Route path="users/new" element={<CreateUserPage />} />
              <Route path="users/edit/:userId" element={<EditUserPage />} />
              <Route path="tags" element={<ManageTagsPage />} /> 
              <Route path="analytics" element={<ViewAnalyticsPage />} />
              <Route path="settings" element={<SiteSettingsPage />} />
              <Route path="contact-submissions" element={<ManageContactSubmissionsPage />} />
              <Route path="content/about-us" element={<EditAboutUsPage />} />
              <Route path="content/homepage" element={<EditHomepageContentPage />} /> {/* <-- NEW ROUTE --> */}
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  const fetchSiteSettings = useSiteSettingsStore(state => state.fetchSiteSettings);
  const publicSettings = useSiteSettingsStore(state => state.publicSettings);
  const isLoadingSettings = useSiteSettingsStore(state => state.isLoading);
  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

  useEffect(() => {
    fetchSiteSettings(); 
  }, [fetchSiteSettings]);

  useEffect(() => {
    if (!isLoadingSettings) {
      const title = publicSettings?.site_title || 'Vedas Trading';
      document.title = title;

      const faviconUrl = publicSettings?.site_favicon_url;
      const faviconLink = document.getElementById('favicon-link') as HTMLLinkElement | null;
      if (faviconLink) {
        if (faviconUrl) {
           if (faviconUrl.startsWith('http') || faviconUrl.startsWith('/static/')) {
            faviconLink.href = faviconUrl.startsWith('/') ? `${backendBaseUrl}${faviconUrl}` : faviconUrl;
          } else { 
            faviconLink.href = `${backendBaseUrl}/static/${faviconUrl}`;
          }
        } else {
          faviconLink.href = '/default_favicon.ico'; 
        }
      }

      const metaDescContent = publicSettings?.seo_meta_description || 
                             'Vedas Trading offers expert financial insights, cutting-edge research, and innovative trading solutions.';
      
      let metaDescriptionElement = document.querySelector('meta[name="description"]');
      if (!metaDescriptionElement) {
        metaDescriptionElement = document.createElement('meta');
        metaDescriptionElement.setAttribute('name', 'description');
        const head = document.getElementsByTagName('head')[0];
        if (head) { head.appendChild(metaDescriptionElement); }
      }
      if (metaDescriptionElement) { metaDescriptionElement.setAttribute('content', metaDescContent); }
    }
  }, [publicSettings, isLoadingSettings, backendBaseUrl]);

  return (
    <Router>
      <GaPageTracker />
      <AppContentController />
      <Toaster richColors position="top-right" closeButton />
    </Router>
  );
}

export default App;
